import useRisksmartUser from '@risksmart-app/components/src/hooks/useRisksmartUser';
import type { TypedPropertyFilterToken } from '@risksmart-app/components/src/table/tableUtils';
import type { GetMyItemsDashboardQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { isEqual, uniqWith } from 'lodash';
import { useCallback } from 'react';

import type { CollectionData } from '@/utils/collectionUtils';

import { resolveEffectiveTarget } from '../../targetUtils';
import { useDashboardStore } from '../../useDashboardStore';

export type FilterableRibbonData = {
  ownerGroups?: CollectionData<
    GetMyItemsDashboardQuery['action'][0]['ownerGroups']
  >;
  contributorGroups?: CollectionData<
    GetMyItemsDashboardQuery['action'][0]['contributorGroups']
  >;
};

/**
 * Provides a function that will build up the URL filtering tokens based on the current
 * filters set on the dashboard (used for click-through functionality on the ribbon).
 * @returns getMyItemsFilteringTokens function
 */
export const useGetMyItemsFilteringTokens = <T>() => {
  const { user } = useRisksmartUser();
  const {
    myItemsFilters: { owner, contributor, groupOwner, groupContributor },
    myItemsTarget,
  } = useDashboardStore();

  // Mirrors the substitution used by useGetQueryVariables so click-through
  // tokens scope to the selected target rather than the logged-in user.
  const { effectiveUserId, isTargetGroup, targetGroupId } =
    resolveEffectiveTarget(myItemsTarget, user?.userId);

  const getMyItemsFilteringTokens = useCallback(
    (
      filteringDetails: readonly FilterableRibbonData[] | undefined,
      ownerPropertyKey: string = 'allOwners',
      contributorPropertyKey: string = 'allContributors'
    ) => {
      const tokens = [];

      if (owner && !isTargetGroup) {
        tokens.push({
          propertyKey: ownerPropertyKey,
          value: effectiveUserId,
          operator: '=',
        });
      }
      if (contributor && !isTargetGroup) {
        tokens.push({
          propertyKey: contributorPropertyKey,
          value: effectiveUserId,
          operator: '=',
        });
      }

      if (groupOwner) {
        if (isTargetGroup) {
          tokens.push({
            propertyKey: ownerPropertyKey,
            value: targetGroupId,
            operator: '=',
          });
        } else if (
          filteringDetails &&
          filteringDetails.some((d) => d.ownerGroups)
        ) {
          const flatOwnerGroups = filteringDetails.flatMap(
            (d) => d.ownerGroups!
          );
          const groupTokens = flatOwnerGroups
            .filter((g) =>
              g.group?.users.some((u) => u.UserId === effectiveUserId)
            )
            .map((g) => ({
              propertyKey: ownerPropertyKey,
              value: g.UserGroupId,
              operator: '=',
            }));
          if (groupTokens.length > 0) {
            tokens.push(...groupTokens);
          }
        }
      }

      if (groupContributor) {
        if (isTargetGroup) {
          tokens.push({
            propertyKey: contributorPropertyKey,
            value: targetGroupId,
            operator: '=',
          });
        } else if (
          filteringDetails &&
          filteringDetails.some((d) => d.contributorGroups)
        ) {
          const flatContributorGroups = filteringDetails.flatMap(
            (d) => d.contributorGroups!
          );
          const groupTokens = flatContributorGroups
            .filter((g) =>
              g.group?.users.some((u) => u.UserId === effectiveUserId)
            )
            .map((g) => ({
              propertyKey: contributorPropertyKey,
              value: g.UserGroupId,
              operator: '=',
            }));
          if (groupTokens.length > 0) {
            tokens.push(...groupTokens);
          }
        }
      }

      return uniqWith(tokens, isEqual) as TypedPropertyFilterToken<T>[];
    },
    [
      contributor,
      groupContributor,
      groupOwner,
      owner,
      effectiveUserId,
      isTargetGroup,
      targetGroupId,
    ]
  );

  return { getMyItemsFilteringTokens };
};
